import { App, Button, Drawer, Form, Space } from 'antd';
import { useLayoutEffect, useState } from 'react';
import {
  buildProjectPayload,
  ProjectFormFields,
  projectFormValuesFromDetail,
  type ProjectFormValues,
} from '@/pages/admin/projects/ProjectFormFields';
import { ApiError } from '@/services/api/client';
import * as projectsApi from '@/services/api/projects';
import type { ProjectDetail, UpdateProjectPayload } from '@/types/project';

interface ProjectEditDrawerProps {
  open: boolean;
  project: ProjectDetail | null;
  onClose: () => void;
  onSaved: () => void;
}

export function ProjectEditDrawer({ open, project, onClose, onSaved }: ProjectEditDrawerProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<ProjectFormValues>();
  const [saving, setSaving] = useState(false);

  useLayoutEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [form, open, project?.id]);

  const handleSubmit = async () => {
    if (!project || saving) {
      return;
    }

    let values: ProjectFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload = buildProjectPayload(values, 'update');
    const changes = Object.fromEntries(
      Object.entries(payload).filter(([field]) =>
        form.isFieldTouched(
          (field === 'startDate' || field === 'endDate' ? 'dateRange' : field) as keyof ProjectFormValues,
        ),
      ),
    ) as UpdateProjectPayload;
    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await projectsApi.updateProject(project.id, changes);
      message.success('项目已更新');
      onSaved();
      onClose();
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '更新项目失败';
      message.error(text);
    } finally {
      setSaving(false);
    }
  };

  const formKey = project ? `edit-${project.id}` : 'edit-empty';
  const initialValues = project ? projectFormValuesFromDetail(project) : undefined;

  return (
    <Drawer
      title="编辑项目"
      width={760}
      open={open}
      onClose={onClose}
      forceRender
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={saving} onClick={() => void handleSubmit()}>
            保存
          </Button>
        </Space>
      }
    >
      {project && initialValues ? (
        <Form
          key={formKey}
          form={form}
          layout="vertical"
          initialValues={initialValues}
        >
          <ProjectFormFields form={form} projectId={project.id} />
        </Form>
      ) : null}
    </Drawer>
  );
}
